import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { RouteGuardPermissions } from "@/components/permissions/route-guard-multiple";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  Crown,
  ClipboardList,
  CalendarClock,
  AlertCircle,
  User,
} from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/operations/work-orders/_list/mine",
)({
  component: MyWorkOrdersPage,
});

const STATUS_CONFIG: Record<
  string,
  { label: string; variant: "default" | "secondary" | "outline" | "destructive" }
> = {
  draft: { label: "Borrador", variant: "secondary" },
  scheduled: { label: "Programada", variant: "outline" },
  in_progress: { label: "En Progreso", variant: "default" },
  completed: { label: "Completada", variant: "secondary" },
  cancelled: { label: "Cancelada", variant: "destructive" },
};

const PERSONAL_STATUS_LABELS: Record<string, string> = {
  assigned: "Asignado",
  in_progress: "Trabajando",
  completed: "Completado",
};

function formatDate(ts: number | undefined) {
  if (!ts) return "---";
  return new Date(ts).toLocaleDateString("es-HN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function WorkOrderCardSkeleton() {
  return (
    <Card className="py-0">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="space-y-1.5 flex-1">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-48" />
          </div>
          <Skeleton className="h-5 w-20 rounded-full" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-24" />
        </div>
      </CardContent>
    </Card>
  );
}

function MyWorkOrdersPage() {
  return (
    <RouteGuardPermissions permissions={[{ module: "work_orders", actions: ["read"] }]}>
      <MyWorkOrdersContent />
    </RouteGuardPermissions>
  );
}

function MyWorkOrdersContent() {
  const result = useQuery(
    api.companies.operations.work_orders.queries.getMyWorkOrders,
  );

  if (result === undefined) {
    return (
      <div className="space-y-3 p-1">
        {[1, 2, 3].map((i) => <WorkOrderCardSkeleton key={i} />)}
      </div>
    );
  }

  if (!result.isLinked) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 gap-4 text-center">
        <div className="p-4 bg-muted rounded-full">
          <User className="h-8 w-8 text-muted-foreground" />
        </div>
        <div>
          <h3 className="text-base font-semibold">Sin perfil de personal vinculado</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-xs">
            Tu cuenta de usuario no está vinculada a un registro de personal.
            Contacta al administrador para que lo configure.
          </p>
        </div>
      </div>
    );
  }

  if (result.workOrders.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 gap-4 text-center">
        <div className="p-4 bg-muted rounded-full">
          <ClipboardList className="h-8 w-8 text-muted-foreground" />
        </div>
        <div>
          <h3 className="text-base font-semibold">Sin órdenes asignadas</h3>
          <p className="text-sm text-muted-foreground mt-1">
            No tienes órdenes de trabajo asignadas actualmente.
          </p>
        </div>
      </div>
    );
  }

  // Separar activas de completadas/canceladas
  const active = result.workOrders.filter(
    (wo) => wo.status !== "completed" && wo.status !== "cancelled",
  );
  const closed = result.workOrders.filter(
    (wo) => wo.status === "completed" || wo.status === "cancelled",
  );

  return (
    <div className="space-y-4 overflow-y-auto h-full p-1 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:bg-border/50">
      {active.length > 0 && (
        <section className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-0.5">
            Activas · {active.length}
          </p>
          {active.map((wo) => (
            <WorkOrderCard key={wo._id} wo={wo} />
          ))}
        </section>
      )}
      {closed.length > 0 && (
        <section className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-0.5">
            Cerradas · {closed.length}
          </p>
          {closed.map((wo) => (
            <WorkOrderCard key={wo._id} wo={wo} />
          ))}
        </section>
      )}
    </div>
  );
}

function WorkOrderCard({ wo }: { wo: any }) {
  const status = STATUS_CONFIG[wo.status ?? "draft"] ?? STATUS_CONFIG.draft;
  const assignmentStatus = PERSONAL_STATUS_LABELS[wo.myAssignment?.status ?? "assigned"] ?? "Asignado";

  return (
    <Link to="/operations/work-orders/$id" params={{ id: wo._id }} className="block">
      <Card className="py-0 hover:shadow-md transition-shadow cursor-pointer">
        <CardContent className="p-3 sm:p-4">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1 space-y-0.5">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-mono text-muted-foreground">
                  {wo.code ?? "---"}
                </span>
                {wo.myAssignment?.isLead && (
                  <span className="flex items-center gap-0.5 text-[10px] text-amber-600 font-medium">
                    <Crown className="h-2.5 w-2.5" />
                    Responsable
                  </span>
                )}
              </div>
              <p className="text-sm font-medium truncate">
                {wo.title ?? "Sin título"}
              </p>
              {wo.client && (
                <p className="text-xs text-muted-foreground truncate">
                  {wo.client.name}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <Badge variant={status.variant} className="text-[10px] h-5">
                {status.label}
              </Badge>
              <span
                className={cn(
                  "text-[10px] px-1.5 py-0.5 rounded-full border",
                  wo.myAssignment?.status === "completed"
                    ? "border-green-200 text-green-700 bg-green-50 dark:border-green-800 dark:text-green-400 dark:bg-green-950/30"
                    : wo.myAssignment?.status === "in_progress"
                      ? "border-blue-200 text-blue-700 bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:bg-blue-950/30"
                      : "border-border text-muted-foreground",
                )}
              >
                {assignmentStatus}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-2.5 text-[10px] text-muted-foreground">
            {wo.scheduledDate && (
              <span className="flex items-center gap-1">
                <CalendarClock className="h-2.5 w-2.5" />
                {formatDate(wo.scheduledDate)}
              </span>
            )}
            {wo.myAssignment?.roleSnapshot && (
              <span className="flex items-center gap-1">
                <User className="h-2.5 w-2.5" />
                {wo.myAssignment.roleSnapshot}
              </span>
            )}
            {wo.myAssignment?.hoursWorked !== undefined && (
              <span className="flex items-center gap-1">
                {wo.myAssignment.hoursWorked}h trabajadas
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
